import { useWorkPeriodStore } from "@/store/work-periods.store.ts"
import { storeToRefs } from "pinia"
import { WorkPeriodController } from "@/controllers/model-controllers/work-period-controller.ts"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import type { WorkPeriod } from "@/models/work-period.model.ts"
import type { Moment } from "moment/moment"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import { syncWorkPeriod } from "@/mobile_app/services/tracking-service"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

export class AppWorkPeriodController extends WorkPeriodController {
  constructor() {
    const workPeriodStore = useWorkPeriodStore()
    const { appWorkPeriods } = storeToRefs(workPeriodStore)
    const storeFunctionsConfig = {
      collection: appWorkPeriods,
      managed: false
    }
    super(storeFunctionsConfig)
  }

  // Synchronize only persisted results: failed saves must leave tracking unchanged.
  private async synchronizeTracking(workPeriod: WorkPeriod | null, deletedWorkPeriodId?: string) {
    try {
      await syncWorkPeriod(workPeriod, deletedWorkPeriodId)
    } catch (error) {
      // A device failure must not make a successful server mutation appear to have failed.
      console.error("Failed to synchronize shift tracking:", error)
    }
  }

  public override async create(
    instance: WorkPeriod,
    config?: IRepositoryRequestConfig,
    override = false
  ) {
    const created = await super.create(instance, config, override)
    if (created) await this.synchronizeTracking(created)
    return created
  }

  public override async update(
    instance: WorkPeriod,
    config?: IRepositoryRequestConfig,
    override: boolean | (() => boolean) = false
  ) {
    const updated = await super.update(instance, config, override)
    if (updated) await this.synchronizeTracking(updated)
    return updated
  }

  public override async delete(
    instance: WorkPeriod,
    config?: IRepositoryRequestConfig,
    waitForResponse = true
  ) {
    const deleted = await super.delete(instance, config, waitForResponse)
    if (deleted) await this.synchronizeTracking(null, instance.id)
    return deleted
  }

  public getListForKnownBusinessEntityContactAndDate(
    kbec: KnownBusinessEntityContact,
    date: Moment
  ) {
    const workPeriodStore = useWorkPeriodStore()
    const { appWorkPeriodsBeingFetchedCount } = storeToRefs(workPeriodStore)
    return super.getListForKnownBusinessEntityContactAndDate(
      kbec,
      date,
      appWorkPeriodsBeingFetchedCount,
      true
    )
  }

  public getIncompleteWorkPeriodForKnownBusinessEntity(pbu: KnownBusinessEntityContact) {
    const query = new ODataQueryBuilder().eq("owner_id", pbu.id).eq("time_end", null).deep() // Important, else we will have validation issues on work period update
    return this.getList({ query })
  }

  /**
   * Registers a work period that was already persisted server-side (e.g. created atomically from
   * a template by a different controller) into the store.
   */
  public async addExistingWorkPeriod(workPeriod: WorkPeriod): Promise<WorkPeriod> {
    const stored = await this.storeFunctions.createModel(workPeriod)
    await this.synchronizeTracking(stored)
    return stored
  }
}
